import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import Button from "../../components/Button.jsx";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ProjectApi} from "../../api/authority/generated/index";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {Trans, useTranslation} from "react-i18next";
import {formatReportingDateRange} from "../../utils/reportingDateFormat.js";
import {effectiveReportingRange, isSnapshotPeriodIncomplete, snapshotHasPublishedDays, snapshotPeriodValue, snapshotRowsForPublishedDays} from "../../utils/reportingSnapshotAvailability.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import {ProfitabilityMetricTooltip} from "../../components/reporting/ProfitabilityMetricTooltip.jsx";
import {calculatePnoPercent, divideRatio, nullableMoneyMajorUnitAmount} from "../../utils/reportingMetricRatios.js";
import {assignedMarketingChartPoint, partialProfitabilityChartPoint, selectProfitabilityMetric, selectReportProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";
import PurchaseCostCoveragePanel from "../dashboard/OrderDashboard/PurchaseCostCoveragePanel.jsx";
import {calculateMarketingPoas, calculateMarketingRoas, combineMediaSourceQualities, mediaSourceQualityBlocksMetrics, normalizeMediaSourceQuality, poasUnavailableCopy, poasUnavailableReasons} from "../../utils/reportingMetricPresentation.js";
import {
    buildChannelRow,
    channelSpendChartPoint,
    channelSpendPresentation,
    snapshotStatusMediaSourceQuality,
    startCancelableReportingRequest,
    ChannelMixColors,
    channelRowName,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartMultiplier,
    formatChartNumber,
    KpiCard,
    MetricBasis,
    MetricHelpLabel,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    currencyUnit,
    ReportingPageDescription,
    snapshotCompletenessAlert,
    SortableHeader,
    useSortableRows,
} from "./ReportingLab.jsx";

function marketingTableValue(row, key) {
    return {
        channel: row.channel?.name,
        marketing: row.spendMarketing,
        revenue: row.turnover,
        profit: row.profit,
        pno: row.pno,
        roas: row.roas,
        poas: row.poas,
        clicks: row.clicks,
        impressions: row.impressions,
        ctr: row.ctr,
        cpc: row.cpc,
        cpm: row.cpm,
        costPerOrder: row.costPerOrder,
        aov: row.averageOrderValue,
        conversion: row.clickConversionRate,
        orders: row.orders,
    }[key];
}

export function MarketingPoasValue({row, renderRatioValue}) {
    return renderRatioValue(row.poas);
}

export function MarketingKpiBasisMeta({t, detail = null}) {
    return <>{t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Bez storen"})}{detail ? ` · ${detail}` : ""}</>;
}

export function MarketingOrderCountMeta({count, renderNumber, t}) {
    return <><strong>{count === null || count === undefined ? "—" : renderNumber(count)}</strong> {t("reporting_lab.marketing.order_count_excluding_cancelled", {defaultValue: "objednávek (bez stornovaných)"})}</>;
}

export function MarketingSystemCard({row, rows, color, renderCurrencyValue, renderNumber, renderPercentValue, renderRatioValue, hasPoas}) {
    const {t, i18n} = useTranslation();

    return <div className="app-surface rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="mb-3 h-1 rounded-full" style={{backgroundColor: color}}/>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t("reporting_lab.marketing.ad_system", {defaultValue: "Reklamní systém"})}</div>
        <div className="mt-1 text-lg font-bold text-slate-900 dark:text-slate-100">{channelRowName(row, rows)}</div>
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm 2xl:grid-cols-3">
            <div>
                <div className="text-xs uppercase text-slate-400">{t("reporting_lab.marketing.costs", {defaultValue: "Náklady"})}</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                    {(() => {
                        const spend = channelSpendPresentation(row);
                        return <>{renderCurrencyValue(spend.value, true)}{spend.partial && <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">{t("reporting_lab.marketing.partial_spend", {defaultValue: i18n.language?.startsWith("cs") ? "Dílčí částka" : "Partial amount"})}</span>}</>;
                    })()}
                </div>
            </div>
            <div>
                <div className="text-xs uppercase text-slate-400"><MetricHelpLabel label={t("reporting_lab.marketing.turnover", {defaultValue: "Obrat"})} tooltip={t("reporting_lab.marketing.attributed_turnover_help", {defaultValue: "Obrat všech objednávek kromě stornovaných přiřazených tomuto reklamnímu systému; nejde o obrat celého obchodu ani o konverzní hodnotu reklamní platformy."})}/></div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{renderCurrencyValue(row.turnover, true)}</div>
            </div>
            <div>
                <div className="text-xs uppercase text-slate-400">{t("reporting_lab.kpi.orders", {defaultValue: "Objednávky"})}</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{renderNumber(row.orders)}</div>
            </div>
            <div>
                <div className="text-xs uppercase text-slate-400">PNO</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{renderPercentValue(row.pno)}</div>
            </div>
            <div>
                <div className="text-xs uppercase text-slate-400"><MetricHelpLabel label={hasPoas ? "ROAS / POAS" : "ROAS"} tooltip={<>{t("reporting_lab.metrics_help.roas", {defaultValue: "ROAS = obrat nestornovaných objednávek / veškeré marketingové náklady za zvolené období a filtry. Náklady se nevylučují podle stavu objednávek."})}{hasPoas && <div className="mt-1"><ProfitabilityMetricTooltip metric="poas" partialProfitability={row.partialProfitability} assignedResult={row.poasUsesAssignedMarketing ? row.partialProfitability?.assignedMarketingResult : null} rowScopes={[row.partialProfitability]} t={t}/></div>}</>}/></div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{renderRatioValue(row.roas)}{hasPoas && <> / <MarketingPoasValue row={row} renderRatioValue={renderRatioValue}/></>}</div>
            </div>
        </div>
    </div>;
}

export function marketingDisplayChannel(channel, t) {
    // Backend's explicit unattributed bucket, not a provider/custom display name.
    return channel?.id === "Unknown" && channel?.name === "Unknown"
        ? {...channel, name: t("reporting_lab.marketing.unknown_channel", {defaultValue: "Neznámý zdroj"})}
        : channel;
}

export function marketingCounterHeaderHelp(t) {
    return t("reporting_lab.marketing.counter_click_help", {
        defaultValue: "Počet vychází z čítače zdroje: kliknutí vykázaná Metou, kliknutí na cíl (TikTok), kliknutí podle poskytovatele (Google, Microsoft, Criteo, Sklik a Zboží.cz), návštěvy Heureky, odchody do e-shopu (GLAMI) nebo otevření detailu Pinu (Pinterest). Čítače se významem liší; nejde jednotně o návštěvy webu ani konverze. U ostatních zdrojů není význam určen.",
    });
}

export function MarketingClickCounterValue({row, renderNumber}) {
    return <span>{renderNumber(row.clicks)}</span>;
}

export function MarketingProfitValue({row, renderCurrencyValue}) {
    const amount = Number(row.profit?.amount);
    return <span className={Number.isFinite(amount) && amount < 0 ? "text-datixo-negative" : undefined}>{renderCurrencyValue(row.profit, true)}</span>;
}

export function marketingCounterCostHelp({currency, since, until}) {
    return <Trans
        i18nKey="reporting_lab.marketing.cost_per_counter_help"
        defaults="Náklady na kliknutí nebo návštěvu v měně {{currency}} za období <period>{{period}}</period> a při zvolených filtrech přehledu. Jde o výpočet Datixa z reklamních nákladů a čítače vykázaného poskytovatelem, nikoli o nativní CPC; údaj je dostupný jen při dostupných nákladech i čítači."
        values={{currency, period: formatReportingDateRange(since, until)}}
        components={{period: <strong className="font-semibold"/>}}
    />;
}

export function purchaseCostCoverageLoadersForRange(effectiveRange, loadOrders, loadOrderItems) {
    return effectiveRange ? {loadOrders, loadOrderItems} : {loadOrders: undefined, loadOrderItems: undefined};
}

const CLICK_BASED_RATIO_SOURCE_TYPES = new Set(["Meta", "Google", "Microsoft", "TikTok", "Criteo", "Sklik", "Zbozi"]);
const KNOWN_SOURCE_TYPES = new Set([...CLICK_BASED_RATIO_SOURCE_TYPES, "Pinterest", "Heureka", "Glami"]);

export function marketingCounterCostLabel(sourceType, t) {
    const keys = {
        Heureka: ["reporting_lab.marketing.cost_per_counter_heureka", "Náklady / návštěvu"],
        Pinterest: ["reporting_lab.marketing.cost_per_counter_pinterest", "Náklady / otevření detailu Pinu"],
        Glami: ["reporting_lab.marketing.cost_per_counter_glami", "Náklady / odchod do e-shopu"],
    };
    const [key, defaultValue] = keys[sourceType]
        ?? (CLICK_BASED_RATIO_SOURCE_TYPES.has(sourceType)
            ? ["reporting_lab.marketing.cost_per_counter_clicks", "Náklady / kliknutí"]
            : ["reporting_lab.marketing.cost_per_counter_unknown", "Náklady / čítač"]);
    return t(key, {defaultValue});
}

export function marketingFinancialVisibility(channelData) {
    const canonicalOverviews = channelData.map(({overview}) => overview).filter((overview) => overview?.financeScope !== "raw_provider");
    const campaigns = canonicalOverviews.flatMap((overview) => (overview?.adAccounts ?? []).flatMap((account) => (account.campaigns ?? []).filter((campaign) => campaign.financeScope !== "raw_provider")));
    const hasAssignedResult = canonicalOverviews.some((overview) => overview?.partialProfitabilityMarketing?.assignedMarketingResult != null);
    const hasGrossProfit = campaigns.some((campaign) => campaign.profitGross !== undefined);
    return {profit: hasGrossProfit || hasAssignedResult, poas: hasGrossProfit || hasAssignedResult};
}

export function formatMarketingMultiplier(value, decimals = 1) {
    return formatChartMultiplier(value, decimals);
}

export function marketingGlobalKpiInputs(channelTotals, response) {
    const hasValue = (field) => Object.prototype.hasOwnProperty.call(response ?? {}, field) && response[field] !== undefined;
    return {
        turnover: hasValue("globalTurnoverMarketing") ? response.globalTurnoverMarketing : {amount: channelTotals.turnover, currency: channelTotals.currency},
        orders: hasValue("globalOrderCountMarketing") ? response.globalOrderCountMarketing : channelTotals.orders,
    };
}

export function marketingAggregateMediaSourceQuality(response, channelRows = []) {
    return response?.mediaSourceQuality
        ?? response?.snapshot?.mediaSourceQuality
        ?? combineMediaSourceQualities(channelRows.map((row) => row?.mediaSourceQuality));
}

export function marketingCostPerOrder(spend, orders, currency, mediaSourceQuality = null) {
    if (mediaSourceQualityBlocksMetrics(mediaSourceQuality)
        || typeof spend !== "number" || !Number.isFinite(spend)
        || typeof orders !== "number" || !Number.isFinite(orders) || orders <= 0) {
        return null;
    }

    return {amount: spend / orders, currency};
}

export function marketingPoasUnavailableReasons(reasons, mediaSourceQuality) {
    if (!mediaSourceQualityBlocksMetrics(mediaSourceQuality)) return reasons;

    const availableReasons = (Array.isArray(reasons) ? reasons : []).filter((reason) => reason !== "zeroSpend");
    return availableReasons.length > 0 ? availableReasons : ["unavailable"];
}

export function marketingHasVerifiedEmptyCompletedCohort({snapshot, response, partialProfitability}) {
    const isExplicitZero = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) && Number(value) === 0;
    return snapshot?.complete === true
        && response?.globalOrderCountMarketing === 0
        && isExplicitZero(response?.globalTurnoverMarketing?.amount)
        && partialProfitability?.complete === true
        && partialProfitability?.available === false
        && isExplicitZero(partialProfitability?.knownLineCount)
        && isExplicitZero(partialProfitability?.unknownLineCount)
        && isExplicitZero(partialProfitability?.estimatedLineCount);
}

export function marketingPnoFromGlobalTurnover(spend, turnover, mediaSourceQuality = null) {
    return calculatePnoPercent(spend, turnover, mediaSourceQuality);
}

export function selectMarketingPoasPresentation({hasPoas, fullPoas, fullComplete, partialProfitability, snapshotIncomplete, mediaSourceQuality}) {
    const qualityBlocked = mediaSourceQualityBlocksMetrics(mediaSourceQuality);
    const safePartialProfitability = qualityBlocked || snapshotIncomplete ? undefined : partialProfitability;
    const standardSelection = selectProfitabilityMetric({
        fullValue: hasPoas && !qualityBlocked ? fullPoas : undefined,
        fullComplete: fullComplete && !qualityBlocked,
        partialProfitability: safePartialProfitability,
        partialField: "knownPoas",
    });

    return selectReportProfitabilityMetric({
        standardSelection,
        partialProfitability: safePartialProfitability,
        assignedField: "poas",
        snapshotIncomplete: snapshotIncomplete || qualityBlocked,
    });
}

export function marketingPoasUnavailablePresentation({snapshot, snapshotIncomplete, poas, selectedCentreSpendAttributionComplete, reasons, t}) {
    if (snapshotIncomplete) {
        return {
            short: snapshotUnavailableShort(snapshot, t),
            detail: t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny za celé období nejsou dostupné."}),
        };
    }

    if ((poas === null || poas === undefined) && selectedCentreSpendAttributionComplete === false) {
        return {
            short: t("reporting_lab.centre_spend_attribution.row_partial_short", {defaultValue: "Nepřiřazená reklama"}),
            detail: t("reporting_lab.centre_spend_attribution.row_partial_help", {defaultValue: "Část reklamních nákladů není přiřazená ke středisku."}),
        };
    }

    return poasUnavailableCopy(reasons, t);
}

export function settleMarketingOverviewRequest(request, {isActive, onSuccess, onError, onFinally}) {
    return request
        .then((result) => {
            if (isActive()) onSuccess(result);
        })
        .catch((error) => {
            if (isActive()) onError(error);
        })
        .finally(() => {
            if (isActive()) onFinally();
        });
}

export function MarketingRequestError({onRetry}) {
    const {t} = useTranslation();
    return <div role="alert" className="app-surface col-span-12 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-datixo-negative dark:border-red-900 dark:bg-red-950/30">
        <p>{t("reporting_lab.marketing.load_error", {defaultValue: "Marketingový přehled se nepodařilo načíst."})}</p>
        <Button variant="outline-black" size="small" className="mt-3" onClick={onRetry}>
            {t("reporting_lab.marketing.retry", {defaultValue: "Zkusit znovu"})}
        </Button>
    </div>;
}

export function buildMarketingChannelRow(channel, overview, selectedCentreSpendAttributionComplete = null) {
    const canonicalOverview = overview && overview.financeScope !== "raw_provider"
        ? {...overview, adAccounts: (overview.adAccounts ?? []).map((account) => ({
            ...account,
            campaigns: (account.campaigns ?? []).filter((campaign) => campaign.financeScope !== "raw_provider"),
        }))}
        : overview;
    let row = buildChannelRow(channel, canonicalOverview, MetricBasis.Marketing);
    const assignedMarketingResult = overview?.partialProfitabilityMarketing?.assignedMarketingResult;
    const mediaQualityBlocked = overview?.financeScope === "raw_provider" || mediaSourceQualityBlocksMetrics(overview?.mediaSourceQuality);
    if (!KNOWN_SOURCE_TYPES.has(channel?.sourceType)) {
        row = {...row, ctr: null, cpc: null, cpm: null, clickConversionRate: null};
    } else if (channel.sourceType === "Heureka" || channel.sourceType === "Glami") {
        row = {...row, ctr: null};
        if (channel.sourceType === "Glami") row.cpm = null;
    }
    if (mediaQualityBlocked) {
        return {
            ...row,
            mediaSourceQuality: overview?.mediaSourceQuality,
            financeScope: overview?.financeScope,
            profit: null,
            profitNet: null,
            profitGross: null,
            contributionBeforeMarketing: null,
            poas: null,
            pno: null,
            roas: null,
            cpc: null,
            cpm: null,
            costPerOrder: null,
            margin: null,
            profitIsPartial: false,
            grossProfitIsPartial: false,
            poasIsPartial: false,
            profitUsesAssignedMarketing: false,
            poasUsesAssignedMarketing: false,
            marginIsPartial: false,
        };
    }
    row = {...row, mediaSourceQuality: overview?.mediaSourceQuality, financeScope: overview?.financeScope};
    if (assignedMarketingResult) {
        return {
            ...row,
            profit: assignedMarketingResult.profitAfterAssignedMarketing ?? null,
            contributionBeforeMarketing: assignedMarketingResult.grossProfit ?? null,
            poas: assignedMarketingResult.poas ?? null,
            profitIsPartial: true,
            grossProfitIsPartial: true,
            poasIsPartial: true,
            profitUsesAssignedMarketing: true,
            poasUsesAssignedMarketing: true,
            margin: null,
            marginIsPartial: false,
        };
    }
    if (row.contributionBeforeMarketing !== null && row.contributionBeforeMarketing !== undefined) return row;

    const broadTurnover = Number(row.turnover?.amount);
    const broadGoodsCost = Number(row.spendGoodsGross?.amount);
    const broadMarketingSpend = Number(row.spendMarketing?.amount);
    const broadMediaQuality = normalizeMediaSourceQuality(overview?.mediaSourceQuality);
    const hasCompleteBroadFinancialBasis = row.profit !== undefined
        && row.purchaseCostQualityAvailable === true
        && row.purchaseCostComplete === true
        && selectedCentreSpendAttributionComplete !== false
        && broadMediaQuality?.available === true && !mediaSourceQualityBlocksMetrics(overview?.mediaSourceQuality)
        && !mediaQualityBlocked
        && row.turnover?.amount !== null && row.turnover?.amount !== undefined && Number.isFinite(broadTurnover)
        && row.spendGoodsGross?.amount !== null && row.spendGoodsGross?.amount !== undefined && Number.isFinite(broadGoodsCost)
        && row.spendMarketing?.amount !== null && row.spendMarketing?.amount !== undefined && Number.isFinite(broadMarketingSpend);

    if (hasCompleteBroadFinancialBasis) {
        const grossProfit = {amount: broadTurnover - broadGoodsCost, currency: row.turnover?.currency ?? row.spendGoodsGross?.currency ?? row.spendMarketing?.currency};
        return {
            ...row,
            profit: {amount: grossProfit.amount - broadMarketingSpend, currency: grossProfit.currency},
            contributionBeforeMarketing: grossProfit,
            poas: broadTurnover < 0 ? null : divideRatio(grossProfit.amount, broadMarketingSpend),
            profitIsPartial: false,
            grossProfitIsPartial: false,
            poasIsPartial: false,
            profitUsesAssignedMarketing: false,
            poasUsesAssignedMarketing: false,
        };
    }
    if (row.contributionBeforeMarketing !== undefined) return row;

    // OverviewData emits numeric profitGross only for complete costs, as R-C-A.
    // Profit permission is independent of PurchasePrice; recover G without
    // requiring or reconstructing a purchase-price field hidden by the API.
    const campaigns = row.campaigns;
    const hasCompleteProfit = campaigns.length > 0 && campaigns.every((campaign) =>
        campaign.profitGross?.amount != null && Number.isFinite(Number(campaign.profitGross.amount))
        && campaign.spendMarketing?.amount != null && Number.isFinite(Number(campaign.spendMarketing.amount)));
    if (!hasCompleteProfit || !row.purchaseCostComplete) return row;

    const grossProfit = campaigns.reduce((sum, campaign) => sum + Number(campaign.profitGross.amount) + Number(campaign.spendMarketing.amount), 0);
    const contributionBeforeMarketing = {amount: grossProfit, currency: row.turnover?.currency ?? row.spendMarketing?.currency};
    return {
        ...row,
        contributionBeforeMarketing,
        poas: row.turnover?.amount == null || Number(row.turnover.amount) < 0 ? null : divideRatio(grossProfit, row.spendMarketing?.amount),
    };
}

export default function ReportingMarketingLab({project}) {
    const {t, i18n} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed, customerWholesaleRequestFilter} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const renderRatioValue = (value) => formatMarketingMultiplier(value);
    const [channelData, setChannelData] = useState([]);
    const [globalKpiResponse, setGlobalKpiResponse] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [partialProfitability, setPartialProfitability] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const [requestError, setRequestError] = useState(false);
    const [retryKey, setRetryKey] = useState(0);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const coverageRange = effectiveReportingRange(snapshot, sinceString, untilString);
    const purchaseCoverageRange = loading ? null : coverageRange;
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        ...customerWholesaleRequestFilter,
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey, customerWholesaleRequestFilter]);

    const loadPurchaseCostCoverageOrders = useCallback(({since, until, selectedCentreIds, selectedDomainIds, page, pageSize}) => new ErpCalculationApi(getAuthorityConfiguration()).purchaseCostCoverage({
        projectId: project.id,
        page,
        pageSize,
        reportContext: "marketing",
        erpFilter: {...customerWholesaleRequestFilter, since, until, selectedCentreIds, selectedDomainIds},
    }), [getAuthorityConfiguration, project.id, customerWholesaleRequestFilter]);
    const loadPurchaseCostCoverageItems = useCallback(({orderId, since, until, selectedCentreIds, selectedDomainIds, page, pageSize}) => new ErpCalculationApi(getAuthorityConfiguration()).purchaseCostCoverageOrderItems({
        projectId: project.id,
        orderId,
        page,
        pageSize,
        reportContext: "marketing",
        erpFilter: {...customerWholesaleRequestFilter, since, until, selectedCentreIds, selectedDomainIds},
    }), [getAuthorityConfiguration, project.id, customerWholesaleRequestFilter]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        setLoading(true);
        setRequestError(false);
        setChannelData([]);
        setGlobalKpiResponse(null);
        setSnapshot(null);
        setPartialProfitability(undefined);

        return startCancelableReportingRequest(
            (signal) => new ProjectApi(getAuthorityConfiguration()).marketingChannelOverviews({projectId: project.id, erpFilter}, {signal}),
            {
                onSuccess: (result) => {
                    setChannelData((result.items ?? []).map((item) => ({
                        channel: {id: item.id, name: item.name, sourceType: item.sourceType},
                        overview: item.overview,
                    })));
                    setSnapshot(result.snapshot);
                    setPartialProfitability(result.partialProfitabilityMarketing);
                    setGlobalKpiResponse(result);
                },
                onError: (error) => {
                    setRequestError(true);
                    et(error).then((message) => { if (message) toast.error(message); });
                },
                onFinally: () => setLoading(false),
            },
        );
    }, [project?.id, erpFilter, retryKey]);

    const channelRows = useMemo(() => {
        const rows = channelData
            .map(({channel, overview}) => buildMarketingChannelRow(marketingDisplayChannel(channel, t), overview, globalKpiResponse?.selectedCentreSpendAttributionComplete))
            .filter((row) => row.financeScope !== "raw_provider" && (moneyAmount(row.spendMarketing) > 0 || moneyAmount(row.turnover) > 0 || row.orders > 0))
            .sort((a, b) => moneyAmount(b.spendMarketing) - moneyAmount(a.spendMarketing));
        return snapshotRowsForPublishedDays(rows, snapshot);
    }, [channelData, snapshot, t]);
    const {sortedRows: sortedTableRows, sort: tableSort, toggleSort} = useSortableRows(
        channelRows,
        "marketing",
        marketingTableValue,
    );
    const financialVisibility = marketingFinancialVisibility(channelData);
    const hasProfit = financialVisibility.profit;
    const hasPoas = financialVisibility.poas;

    const totals = useMemo(() => {
        return channelRows.reduce((accumulator, row) => {
            accumulator.spend += moneyAmount(row.spendMarketing);
            accumulator.turnover += moneyAmount(row.turnover);
            accumulator.orders += row.orders;
            accumulator.clicks += row.clicks;
            accumulator.impressions += row.impressions;
            return accumulator;
        }, {spend: 0, turnover: 0, orders: 0, clicks: 0, impressions: 0});
    }, [channelRows]);

    const currency = channelRows.find((row) => row.spendMarketing?.currency)?.spendMarketing?.currency ?? projectCurrency(project);
    const globalKpiInputs = marketingGlobalKpiInputs({...totals, currency}, globalKpiResponse);
    const channelCountMeta = snapshotHasPublishedDays(snapshot)
        ? t("reporting_lab.marketing.system_count", {defaultValue: "{{count}} systémů", count: channelRows.length})
        : t("reporting_lab.snapshot.no_published_days_short", {defaultValue: "Bez publikovaných dnů"});
    const spendMoney = snapshotPeriodValue({amount: totals.spend, currency}, snapshot);
    const turnoverMoney = snapshotPeriodValue(globalKpiInputs.turnover, snapshot);
    const periodSpend = snapshotPeriodValue(totals.spend, snapshot);
    const periodTurnover = snapshotPeriodValue(globalKpiInputs.turnover?.amount, snapshot);
    const summaryUnavailable = !snapshotHasPublishedDays(snapshot);
    const emptyCompletedPurchaseCohort = marketingHasVerifiedEmptyCompletedCohort({snapshot, response: globalKpiResponse, partialProfitability: globalKpiResponse?.partialProfitabilityMarketing});
    const mediaSourceQuality = marketingAggregateMediaSourceQuality(globalKpiResponse, channelRows);
    const roas = calculateMarketingRoas(periodTurnover, periodSpend, mediaSourceQuality);
    const fullPoas = summaryUnavailable ? null : calculateMarketingPoas(channelRows, mediaSourceQuality);
    const poasPresentation = selectMarketingPoasPresentation({
        hasPoas,
        fullPoas,
        fullComplete: channelRows.length > 0 && channelRows.every((row) => row.purchaseCostComplete === true),
        partialProfitability,
        snapshotIncomplete: summaryUnavailable,
        mediaSourceQuality,
    });
    const poas = poasPresentation.value ?? null;
    const poasRows = channelRows.flatMap((row) => row.campaigns ?? []);
    const poasReasons = summaryUnavailable ? [] : marketingPoasUnavailableReasons(poasUnavailableReasons(poasRows, {
        revenue: "turnoverGross",
        spend: "spendMarketing",
        costComplete: "purchaseCostCompleteGross",
        costUnknown: "purchaseCostUnknownGross",
        costEstimated: "purchaseCostEstimatedGross",
    }, poas === null), mediaSourceQuality);
    const poasUnavailable = marketingPoasUnavailablePresentation({
        snapshot,
        snapshotIncomplete: summaryUnavailable,
        poas,
        selectedCentreSpendAttributionComplete: globalKpiResponse?.selectedCentreSpendAttributionComplete,
        reasons: poasReasons,
        t,
    });
    const hasCompletePnoInputs = channelRows.every((row) => row.spendMarketing?.amount != null)
        && periodTurnover != null;
    const pno = !summaryUnavailable && hasCompletePnoInputs ? marketingPnoFromGlobalTurnover(periodSpend, periodTurnover, mediaSourceQuality) : null;
    const selectedCentrePnoAttributionHelp = (centresAllowListed ?? []).length > 0
        && globalKpiResponse?.selectedCentreSpendAttributionComplete === false
        ? t("reporting_lab.centre_spend_attribution.row_partial_help", {defaultValue: "Část reklamních nákladů není přiřazená ke středisku."})
        : null;
    const pnoMetricTooltip = <>
        <div>{t("reporting_lab.metrics_help.marketing_pno", {defaultValue: "PNO = veškeré marketingové náklady za zvolené období a filtry / obrat nestornovaných objednávek × 100%. Náklady se nevylučují podle stavu objednávek."})}</div>
        <div className="mt-1">{t("reporting_lab.metrics_help.marketing_roas", {defaultValue: "ROAS = obrat nestornovaných objednávek / veškeré marketingové náklady za zvolené období a filtry."})}</div>
        {selectedCentrePnoAttributionHelp && <div className="mt-1">{selectedCentrePnoAttributionHelp}</div>}
    </>;
    const periodOrders = snapshotPeriodValue(globalKpiInputs.orders, snapshot);
    const cpa = marketingCostPerOrder(periodSpend, periodOrders, currency, mediaSourceQuality);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const marketingComparisonOption = {
        color: ["#7c3aed", "#2563eb", "#16a34a", "#111827"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 38, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, [
                (value) => formatChartCurrency(value, currency),
                (value) => formatChartCurrency(value, currency),
                (value) => formatChartCurrency(value, currency),
                formatMarketingMultiplier,
            ]),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {type: "category", data: channelRows.slice(0, 8).map((row) => channelRowName(row, channelRows))},
        yAxis: [
            {
                type: "value",
                name: currencyUnit(currency),
                axisLabel: {
                    fontSize: 12,
                    formatter: formatChartNumber,
                },
            },
            {
                type: "value",
                name: "ROAS",
                axisLabel: {
                    fontSize: 12,
                    formatter: formatMarketingMultiplier,
                },
            },
        ],
        series: [
            {
                name: t("reporting_lab.series.marketing", {defaultValue: "Marketing"}),
                type: "bar",
                data: channelRows.slice(0, 8).map(channelSpendChartPoint),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.series.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: channelRows.slice(0, 8).map((row) => moneyToMajor(row.turnover)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"}),
                type: "bar",
                data: channelRows.slice(0, 8).map((row) => row.profitUsesAssignedMarketing ? assignedMarketingChartPoint(nullableMoneyMajorUnitAmount(row.profit), row.partialProfitability, t) : partialProfitabilityChartPoint(nullableMoneyMajorUnitAmount(row.profit), row.partialProfitability, row.profitIsPartial, t)),
                barMaxWidth: 28,
            },
            {
                name: "ROAS",
                type: "line",
                yAxisIndex: 1,
                smooth: true,
                symbolSize: 7,
                data: channelRows.slice(0, 8).map((row) => row.roas),
            },
        ].filter((series) => hasProfit || series.name !== t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"})),
    };

    const tableColumns = [
        {key: "channel", label: t("reporting_lab.table.channel", {defaultValue: "Kanál"}), render: (row) => channelRowName(row, channelRows), align: "left"},
        {key: "marketing", label: t("reporting_lab.table.marketing", {defaultValue: "Marketing"}), render: (row) => {
            const spend = channelSpendPresentation(row);
            return <>{renderCurrencyValue(spend.value, true)}{spend.partial && <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">{t("reporting_lab.marketing.partial_spend", {defaultValue: i18n.language?.startsWith("cs") ? "Dílčí částka" : "Partial amount"})}</span>}</>;
        }},
        {key: "revenue", label: t("reporting_lab.table.revenue", {defaultValue: "Obrat"}), render: (row) => renderCurrencyValue(row.turnover, true)},
        {key: "profit", label: <span className="inline-flex flex-col"><span>{t("reporting_lab.marketing.gross_profit_header", {defaultValue: "Hrubý zisk"})}</span>{" "}<span>{t("reporting_lab.marketing.after_ads_header", {defaultValue: "po reklamě"})}</span></span>, tooltip: <ProfitabilityMetricTooltip metric="profit" partialProfitability={partialProfitability} rowScopes={channelRows.map((row) => row.partialProfitability)} assignedScope={channelRows.some((row) => row.profitUsesAssignedMarketing)} t={t}/>, render: (row) => <MarketingProfitValue row={row} renderCurrencyValue={renderCurrencyValue}/>},
        {key: "pno", label: "PNO", tooltip: pnoMetricTooltip, render: (row) => renderPercentValue(row.pno)},
        {key: "roas", label: "ROAS", render: (row) => renderRatioValue(row.roas)},
        {key: "poas", label: "POAS", tooltip: <ProfitabilityMetricTooltip metric="poas" partialProfitability={partialProfitability} rowScopes={channelRows.map((row) => row.partialProfitability)} assignedScope={channelRows.some((row) => row.poasUsesAssignedMarketing)} t={t}/>, render: (row) => <MarketingPoasValue row={row} renderRatioValue={renderRatioValue}/>},
        {key: "clicks", label: t("reporting_lab.marketing.counter_header", {defaultValue: "Kliknutí"}), tooltip: marketingCounterHeaderHelp(t), render: (row) => <MarketingClickCounterValue row={row} renderNumber={renderNumber}/>},
        {key: "impressions", label: t("reporting_lab.table.impressions", {defaultValue: "Imprese"}), render: (row) => renderNumber(row.impressions)},
        {key: "ctr", label: "CTR", tooltip: t("reporting_lab.marketing.ctr_provider_help", {defaultValue: "CTR = kliknutí vykázaná poskytovatelem / zobrazení. U Pinterestu jde o otevření detailu Pinu, nikoli proklik do e-shopu."}), render: (row) => <span title={row.channel?.sourceType === "Pinterest" ? t("reporting_lab.marketing.ctr_pinterest_help", {defaultValue: "CTR poskytovatele z otevření detailu Pinu (CLICKTHROUGH_1) a zobrazení; otevření Pinu není kliknutí do e-shopu."}) : undefined}>{renderPercentValue(row.ctr)}</span>},
        {key: "cpc", label: t("reporting_lab.marketing.cost_per_counter_header", {defaultValue: "CPC"}), tooltip: marketingCounterCostHelp({currency, since: sinceString, until: untilString}), render: (row) => <span title={marketingCounterCostLabel(row.channel?.sourceType, t)}>{renderCurrencyValue(row.cpc, true)}</span>},
        {key: "cpm", label: "CPM", tooltip: t("reporting_lab.marketing.cpm_provider_help", {defaultValue: "CPM vypočtené Datixem = reklamní náklady tohoto systému / zobrazení vykázaná stejným systémem × 1 000. CPM uvedené přímo v reklamním systému se může lišit."}), render: (row) => renderCurrencyValue(row.cpm, true)},
        {key: "costPerOrder", label: t("reporting_lab.table.cost_per_order", {defaultValue: "Cena / obj."}), render: (row) => renderCurrencyValue(row.costPerOrder, true)},
        {key: "aov", label: t("reporting_lab.table.aov", {defaultValue: "AOV"}), render: (row) => renderCurrencyValue(row.averageOrderValue, true)},
        {key: "conversion", label: t("reporting_lab.marketing.orders_per_counter_header", {defaultValue: "Konverze"}), tooltip: t("reporting_lab.marketing.orders_per_counter_help", {defaultValue: "Nestornované objednávky přiřazené tomuto systému, včetně objednávek bez známého stavu, / počet kliknutí, návštěv či jiných interakcí vykázaný platformou × 100 %. Jde o výpočet Datixa, nikoli o konverze vykázané platformou."}), render: (row) => renderPercentValue(row.clickConversionRate)},
        {key: "orders", label: t("reporting_lab.table.orders_short", {defaultValue: "Obj."}), render: (row) => renderNumber(row.orders)},
    ].filter((column) => (hasProfit || column.key !== "profit") && (hasPoas || column.key !== "poas"));

    if (requestError && !loading) {
        return <WideSecureLayout periodReport retentionExpired={retentionExpired}
            title={t("reporting_lab.pages.marketing", {defaultValue: "Marketing"})}
            actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        >
            <MarketingRequestError onRetry={() => setRetryKey((value) => value + 1)}/>
        </WideSecureLayout>;
    }

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.marketing", {defaultValue: "Marketing"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(snapshot, t)]}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.marketing.title", {defaultValue: "K čemu slouží marketingový přehled"})}
                >
                    {hasPoas
                        ? t("reporting_lab.descriptions.marketing.text", {defaultValue: "Souhrn placených i neplacených kanálů: náklady, hodnota objednávek, ROAS, POAS, PNO a rozpad výkonu podle reklamních systémů. Objednávková i ekonomická báze zahrnuje všechny objednávky kromě stornovaných."})
                        : t("reporting_lab.descriptions.marketing.text_without_profit", {defaultValue: "Souhrn placených i neplacených kanálů: náklady, hodnota objednávek, ROAS a PNO. Objednávková báze zahrnuje všechny objednávky kromě stornovaných."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">
                <ReportingSnapshotStatus snapshots={snapshot} mediaSourceQuality={snapshotStatusMediaSourceQuality(snapshot, mediaSourceQuality)}/>
                <PurchaseCostCoveragePanel coveragePercent={emptyCompletedPurchaseCohort ? null : partialProfitability?.revenueCoveragePercent} knownLineCount={emptyCompletedPurchaseCohort ? null : partialProfitability?.knownLineCount} unknownLineCount={emptyCompletedPurchaseCohort ? null : partialProfitability?.unknownLineCount} actionableUnknownLineCount={emptyCompletedPurchaseCohort ? null : partialProfitability?.actionableUnknownLineCount} estimatedLineCount={emptyCompletedPurchaseCohort ? null : partialProfitability?.estimatedLineCount} coverageBasis="non_cancelled" loading={loading} {...purchaseCostCoverageLoadersForRange(purchaseCoverageRange, loadPurchaseCostCoverageOrders, loadPurchaseCostCoverageItems)} requestFilters={purchaseCoverageRange ? {...purchaseCoverageRange, selectedCentreIds: centresAllowListed, selectedDomainIds: domainsAllowListed} : undefined} requestScopeKey={`${project.id}:marketing`}/>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.marketing_spend", {defaultValue: "Marketingové náklady"})} value={renderCurrencyValue(spendMoney, true)} meta={channelCountMeta}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.conversion_value", {defaultValue: "Hodnota objednávek"})} value={renderCurrencyValue(turnoverMoney, true)} meta={<MarketingOrderCountMeta count={periodOrders} renderNumber={renderNumber} t={t}/>}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard
                        loading={loading}
                        label="PNO"
                        value={<span className="flex flex-wrap items-center gap-2">
                            <span>{renderPercentValue(pno)}</span>
                            <span className="rounded-md bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-950/60 dark:text-violet-200">
                                ROAS {renderRatioValue(roas)}
                            </span>
                        </span>}
                        meta={t("reporting_lab.metrics_help.pno", {defaultValue: "Marketingové náklady jako podíl obratu."})}
                        info={pnoMetricTooltip}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                    />
                </div>
                {hasPoas && <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard
                        loading={loading}
                        label="POAS"
                        value={<span className="flex flex-wrap items-center gap-2">
                            <span>{renderRatioValue(poas)}</span>
                            <span className="rounded-md bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-950/60 dark:text-violet-200">
                                {t("reporting_lab.table.cost_per_order", {defaultValue: "Cena / obj."})} {renderCurrencyValue(cpa, true)}
                            </span>
                        </span>}
                        meta={<>{t("reporting_lab.marketing.poas_basis_short", {defaultValue: "Hrubý zisk před reklamou / marketingové náklady"})}{!poasPresentation.partial && poasUnavailable.short ? ` · ${poasUnavailable.short}` : ""}</>}
                        metaTone={poas === null && poasUnavailable.short ? "unavailable" : "default"}
                        info={<><ProfitabilityMetricTooltip metric="poas" partialProfitability={partialProfitability} assignedResult={poasPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} costQuality={{complete: channelRows.every((row) => row.purchaseCostComplete === true), estimatedCount: channelRows.reduce((sum, row) => sum + Number(row.purchaseCostEstimatedCount ?? 0), 0)}} rowScopes={channelRows.map((row) => row.partialProfitability)} t={t}/>{!poasPresentation.partial && poasUnavailable.detail && <div className="mt-1">{poasUnavailable.detail}</div>}</>}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                    />
                </div>}

                <Panel loading={loading} title={t("reporting_lab.marketing.channel_comparison", {defaultValue: "Srovnání reklamních systémů"})} className="col-span-12">
                    <EChart option={marketingComparisonOption} className="h-96"/>
                </Panel>

                <div className="col-span-12 grid grid-cols-12 gap-4">
                    {channelRows.slice(0, 4).map((row, index) => <div key={row.channel.id} className="col-span-12 md:col-span-6 xl:col-span-3">
                        <MarketingSystemCard
                            row={row}
                            rows={channelRows}
                            color={ChannelMixColors[index % ChannelMixColors.length]}
                            renderCurrencyValue={renderCurrencyValue}
                            renderNumber={renderNumber}
                            renderPercentValue={renderPercentValue}
                            renderRatioValue={renderRatioValue}
                            hasPoas={hasPoas}
                        />
                    </div>)}
                </div>

                <Panel
                    loading={loading}
                    title={t("reporting_lab.marketing.system_table", {defaultValue: "Výkon reklamních systémů"})}
                    className="col-span-12"
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                {tableColumns.map((column) => <SortableHeader key={column.key} label={column.label} tooltip={column.tooltip} sortKey={column.key} sort={tableSort} onSort={toggleSort} align={column.align ?? "right"}/>) }
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {sortedTableRows.map((row) => <tr key={row.channel.id}>
                                {tableColumns.map((column) => <td key={column.key} className={`${column.align === "left" ? "" : "text-right"} text-slate-600 dark:text-slate-300`}>{column.render(row)}</td>)}
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
    </WideSecureLayout>;
}
